import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";
import { fmtINR, fmtRelative } from "../lib/format";
import { Building2, Users, Sparkles, IndianRupee, Loader2, ArrowUpCircle, Wallet, TrendingUp, Coins, Cpu, Database, X, Search, Globe, RefreshCw } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, BarChart, Bar, CartesianGrid } from "recharts";

const PIE_COLORS = { free: "#94A3B8", trial: "#F59E0B", standard: "#3B82F6", pro: "#8B5CF6", premium: "#7C5CFF" };
const PLAN_LABELS = { free: "Free", trial: "Trial", standard: "Standard", pro: "Pro", premium: "Premium" };

export default function OverviewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [showSeedModal, setShowSeedModal] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/plans/upgrade-requests"),
      api.get("/billing/overview").catch(() => ({ data: null })),
      api.get("/snapshot").catch(() => ({ data: null })),
    ])
      .then(([ov, ur, bl, sn]) => {
        setData(ov.data);
        setUpgrades((ur.data.items || []).filter((r) => r.status === "pending").slice(0, 5));
        setBilling(bl.data);
        setSnapshot(sn.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-[#7C5CFF] animate-spin" />
      </div>
    );
  }
  if (!data) return <p className="p-8 text-[#5B617A]">Failed to load dashboard.</p>;

  const t = data.totals;
  const planPie = Object.entries(data.plan_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: PLAN_LABELS[k] || k, value: v, color: PIE_COLORS[k] || "#94A3B8" }));
  const weekSeries = data.new_tenants_this_week || [];
  const top5 = data.top_5_tenants_by_ai_usage_30d || [];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto glass-animate" data-testid="overview-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Dashboard</h1>
        <button
          onClick={() => setShowSeedModal(true)}
          data-testid="seed-demo-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100 transition-colors"
        >
          <Database size={15} /> Seed Demo Data
        </button>
      </div>

      {showSeedModal && (
        <SeedDemoModal onClose={() => setShowSeedModal(false)} toast={toast} />
      )}

      {/* Slice A3 — HO Snapshot Quick Stats bar */}
      {snapshot ? (
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="ho-snapshot-bar">
          <SnapshotCard
            testId="snap-active-tenants"
            icon={Building2}
            gradient="from-[#7C5CFF] to-[#5A3FD9]"
            label="Total Active Tenants"
            value={String(snapshot.active_tenants)}
            sub={`${snapshot.total_tenants} total`}
          />
          <SnapshotCard
            testId="snap-todays-revenue"
            icon={Coins}
            gradient="from-emerald-500 to-teal-600"
            label="Today's Revenue"
            value={fmtINR(snapshot.todays_revenue_inr)}
            sub={snapshot.today}
          />
          <SnapshotCard
            testId="snap-ai-spend"
            icon={Cpu}
            gradient="from-violet-500 to-indigo-600"
            label="AI Spend (MTD)"
            value={fmtINR(snapshot.ai_spend_mtd_inr)}
            sub={`${(snapshot.ai_tokens_mtd || 0).toLocaleString()} tokens · ${snapshot.month}`}
          />
        </div>
      ) : null}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile
          icon={Building2}
          label="Active Tenants"
          value={t.tenants_active}
          sub={`${t.tenants_suspended} suspended`}
          color="#7C5CFF"
          testId="kpi-active-tenants"
        />
        <KpiTile
          icon={Users}
          label="Trial Tenants"
          value={t.tenants_trial}
          sub={`${(data.trial_conversion_rate * 100).toFixed(1)}% conversion`}
          color="#F59E0B"
          testId="kpi-trial-tenants"
        />
        <KpiTile
          icon={IndianRupee}
          label="Revenue Processed"
          value={fmtINR(data.revenue_processed_all_tenants)}
          sub="All tenants, all time"
          color="#10B981"
          testId="kpi-revenue"
        />
        <KpiTile
          icon={Sparkles}
          label="AI Calls (Month)"
          value={data.ai_calls_this_month.toLocaleString()}
          sub={`${fmtINR(data.ai_cost_inr_this_month)} est. cost`}
          color="#8B5CF6"
          testId="kpi-ai-calls"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Plan distribution donut */}
        <div className="glass-card p-5" data-testid="plan-distribution-chart">
          <p className="text-sm font-bold text-[#0B0F1A] mb-3">Plan Distribution</p>
          {planPie.length === 0 ? (
            <p className="text-sm text-[#5B617A] mt-4 text-center">No tenants yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={planPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {planPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {planPie.map((e) => (
              <span key={e.name} className="flex items-center gap-1 text-[10px] font-semibold text-[#5B617A]">
                <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />
                {e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>

        {/* New tenants this week */}
        <div className="glass-card p-5" data-testid="new-tenants-chart">
          <p className="text-sm font-bold text-[#0B0F1A] mb-3">New Tenants This Week</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={weekSeries}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#7C5CFF" strokeWidth={2} dot={{ r: 3, fill: "#7C5CFF" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top 5 AI usage */}
        <div className="glass-card p-5" data-testid="top-ai-usage-chart">
          <p className="text-sm font-bold text-[#0B0F1A] mb-3">Top 5 AI Usage (30d)</p>
          {top5.length === 0 ? (
            <p className="text-sm text-[#5B617A] mt-4 text-center">No AI usage data</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={top5} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                <Tooltip formatter={(v) => `${v} calls`} />
                <Bar dataKey="ai_calls" fill="#7C5CFF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent upgrade requests */}
      <div className="glass-card p-5" data-testid="recent-upgrade-requests">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-[#0B0F1A]">Recent Upgrade Requests</p>
          <button
            data-testid="view-all-upgrades"
            onClick={() => navigate("/admin/upgrade-requests")}
            className="text-xs font-semibold text-[#7C5CFF] hover:underline"
          >
            View all →
          </button>
        </div>
        {upgrades.length === 0 ? (
          <p className="text-sm text-[#5B617A]">No pending requests.</p>
        ) : (
          <div className="space-y-2">
            {upgrades.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2 border-b border-[#7C5CFF]/[0.06] last:border-0" data-testid={`upgrade-req-${r.id}`}>
                <ArrowUpCircle className="w-4 h-4 text-[#7C5CFF] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0B0F1A] truncate">{r.tenant_name}</p>
                  <p className="text-[10px] text-[#5B617A]">
                    Wants <span className="font-semibold text-[#7C5CFF]">{r.requested_plan}</span> · {fmtRelative(r.timestamp)}
                  </p>
                </div>
                <button
                  data-testid={`quick-bump-${r.id}`}
                  onClick={() => navigate(`/admin/tenants/${r.tenant_id}`)}
                  className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#7C5CFF]/10 text-[#7C5CFF] hover:bg-[#7C5CFF]/20 transition-colors"
                >
                  Review
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Billing Overview */}
      {billing && (
        <div className="mt-6 space-y-4" data-testid="billing-overview-section">
          <h2 className="text-lg font-bold text-[#0B0F1A]">Billing Overview <span className="text-xs font-normal text-[#5B617A] ml-1">{billing.month_key_ist}</span></h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiTile icon={Wallet} label="Monthly Revenue" value={fmtINR(billing.monthly_revenue_inr)}
              sub={billing.delta_vs_prev_month_pct != null ? `${billing.delta_vs_prev_month_pct > 0 ? "+" : ""}${billing.delta_vs_prev_month_pct}% vs last month` : "No prior data"}
              color="#10B981" testId="kpi-monthly-revenue" />
            <KpiTile icon={IndianRupee} label="ARPU" value={fmtINR(billing.arpu_inr)}
              sub={`${billing.paying_tenants_count} paying tenants`}
              color="#3B82F6" testId="kpi-arpu" />
            <KpiTile icon={TrendingUp} label="Prev Month" value={fmtINR(billing.monthly_revenue_prev_month_inr)}
              sub="Previous month revenue"
              color="#F59E0B" testId="kpi-prev-revenue" />
            <KpiTile icon={IndianRupee} label="Outstanding" value={fmtINR(billing.total_outstanding_inr)}
              sub="All tenants"
              color={billing.total_outstanding_inr > 0 ? "#EF4444" : "#10B981"} testId="kpi-outstanding" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-card p-5" data-testid="top-payers-table">
              <p className="text-sm font-bold text-[#0B0F1A] mb-3">Top 5 Payers This Month</p>
              {(billing.top_5_payers_this_month || []).length === 0 ? (
                <p className="text-sm text-[#5B617A]">No payments this month.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#7C5CFF]/[0.08]">
                      <th className="text-left py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Tenant</th>
                      <th className="text-left py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Plan</th>
                      <th className="text-right py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.top_5_payers_this_month.map((p, i) => (
                      <tr key={p.tenant_id || i} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover" data-testid={`top-payer-${i}`}>
                        <td className="py-2 text-[#0B0F1A] truncate max-w-[140px]">{p.tenant_name || p.tenant_id}</td>
                        <td className="py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#7C5CFF]/[0.08] text-[#5B617A]">{p.plan}</span></td>
                        <td className="py-2 text-right font-semibold text-emerald-700" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(p.amount_inr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="glass-card p-5" data-testid="ai-cost-vs-revenue-table">
              <p className="text-sm font-bold text-[#0B0F1A] mb-3">AI Cost vs Revenue (30d)</p>
              {(billing.ai_cost_vs_revenue || []).length === 0 ? (
                <p className="text-sm text-[#5B617A]">No data available.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#7C5CFF]/[0.08]">
                        <th className="text-left py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Tenant</th>
                        <th className="text-right py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Revenue</th>
                        <th className="text-right py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">AI Cost</th>
                        <th className="text-right py-2 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.ai_cost_vs_revenue.map((r, i) => (
                        <tr key={r.tenant_id || i} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover" data-testid={`margin-row-${i}`}>
                          <td className="py-2 text-[#0B0F1A] truncate max-w-[140px]">{r.tenant_name || r.tenant_id}</td>
                          <td className="py-2 text-right text-emerald-700" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(r.monthly_payment_inr)}</td>
                          <td className="py-2 text-right text-red-600" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(r.ai_cost_inr_30d)}</td>
                          <td className={`py-2 text-right font-semibold ${r.gross_margin_inr >= 0 ? "text-emerald-700" : "text-red-600"}`} style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(r.gross_margin_inr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Country-wise Revenue & Tenants */}
      <CountryOverviewSection />
    </div>
  );
}

const COUNTRY_FLAGS = { IN: "🇮🇳", AE: "🇦🇪", SA: "🇸🇦", QA: "🇶🇦", BH: "🇧🇭", KW: "🇰🇼", OM: "🇴🇲", OTHER: "🌐" };
const CURRENCY_PREFIX = { INR: "₹", AED: "AED ", SAR: "SAR ", QAR: "QAR ", BHD: "BHD ", KWD: "KWD ", OMR: "OMR ", USD: "$" };
const PLAN_CHIP_COLORS = { basic: "bg-slate-100 text-slate-600", standard: "bg-blue-50 text-blue-700", pro: "bg-violet-50 text-violet-700", premium: "bg-[#7C5CFF]/10 text-[#7C5CFF]" };

function fmtCurrencyVal(currencyCode, val) {
  const prefix = CURRENCY_PREFIX[currencyCode] || currencyCode + " ";
  if (val == null || val === 0) return `${prefix}0`;
  try {
    if (currencyCode === "INR") return fmtINR(val);
    return prefix + val.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  } catch { return prefix + val; }
}

function CountryOverviewSection() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(defaultMonth);
  const [metric, setMetric] = useState("subscription");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/dashboard/country-overview?period=${period}&metric=${metric}`);
      setData(res.data);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [period, metric]);

  useEffect(() => { load(); }, [load]);

  const isCombined = metric === "combined";

  return (
    <div className="mt-8" data-testid="country-overview-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#7C5CFF]" />
          <h2 className="text-lg font-bold text-[#0B0F1A]">Country-wise Revenue &amp; Tenants</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
            data-testid="country-ov-month" className="input-field text-xs px-2 py-1.5" />
          <div className="flex rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="country-ov-metric">
            {["subscription", "sales", "combined"].map(m => (
              <button key={m} onClick={() => setMetric(m)}
                data-testid={`metric-${m}`}
                className={`px-3 py-1.5 text-[11px] font-semibold capitalize transition-colors ${metric === m ? "bg-[#7C5CFF] text-white" : "bg-white text-[#5B617A] hover:bg-[#7C5CFF]/[0.06]"}`}>
                {m}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="country-ov-refresh" className="p-1.5 rounded-lg hover:bg-[#7C5CFF]/[0.06] text-[#5B617A]">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#7C5CFF]" /></div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-sm text-[#5B617A] mb-2">Failed to load country overview.</p>
          <button onClick={load} className="text-xs font-semibold text-[#7C5CFF] hover:underline">Retry</button>
        </div>
      ) : (
        <>
          {/* Totals strip */}
          {data?.totals && (
            <div className="flex flex-wrap items-center gap-4 mb-4 px-4 py-2.5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="country-ov-totals">
              <span className="text-xs font-semibold text-[#0B0F1A]">Active: <span className="text-[#7C5CFF]">{data.totals.active_tenants?.toLocaleString()}</span></span>
              <span className="text-xs font-semibold text-[#0B0F1A]">Total: <span className="text-[#7C5CFF]">{data.totals.total_tenants?.toLocaleString()}</span></span>
              <span className="text-xs font-semibold text-[#0B0F1A]">Sales: <span className="text-[#7C5CFF]">{data.totals.sales_count?.toLocaleString()}</span></span>
              <span className="text-[10px] text-[#5B617A] italic ml-auto">Revenue not aggregated across currencies — see per-country figures.</span>
            </div>
          )}

          {/* Country cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="country-cards-grid">
            {(data?.countries || []).map(c => {
              const isEmpty = c.total_tenants === 0;
              return (
                <div key={c.code}
                  data-testid={`country-card-${c.code}`}
                  className={`glass-card p-4 transition-opacity ${isEmpty ? "opacity-50" : ""}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">{COUNTRY_FLAGS[c.code] || "🌐"}</span>
                    <span className="text-sm font-bold text-[#0B0F1A] truncate">{c.name}</span>
                    <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500">{c.currency_code}</span>
                  </div>

                  {isEmpty ? (
                    <p className="text-xs text-[#5B617A] italic py-4 text-center">No tenants yet</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Revenue */}
                      {isCombined ? (
                        <div className="space-y-0.5">
                          <p className="text-[10px] text-[#5B617A] uppercase tracking-wider">Subscription</p>
                          <p className="text-lg font-extrabold text-[#0B0F1A] tabular-nums" data-testid={`rev-sub-${c.code}`}>{fmtCurrencyVal(c.currency_code, c.revenue_subscription)}</p>
                          <p className="text-[10px] text-[#5B617A] uppercase tracking-wider mt-1">Sales</p>
                          <p className="text-lg font-extrabold text-[#0B0F1A] tabular-nums" data-testid={`rev-sales-${c.code}`}>{fmtCurrencyVal(c.currency_code, c.revenue_sales)}</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-[10px] text-[#5B617A] uppercase tracking-wider">Revenue</p>
                          <p className="text-xl font-extrabold text-[#0B0F1A] tabular-nums" data-testid={`rev-${c.code}`}>{fmtCurrencyVal(c.currency_code, c.revenue)}</p>
                        </div>
                      )}

                      {/* Tenant counts */}
                      <p className="text-xs text-[#5B617A]" data-testid={`tenants-${c.code}`}>
                        <span className="font-semibold text-[#0B0F1A]">{c.active_tenants}</span> active · <span className="font-semibold text-[#0B0F1A]">{c.total_tenants}</span> total
                      </p>

                      {/* Sales count */}
                      <p className="text-xs text-[#5B617A]">Sales: <span className="font-semibold text-[#0B0F1A]">{c.sales_count}</span></p>

                      {/* Plan breakdown chips */}
                      {c.plan_breakdown && (
                        <div className="flex flex-wrap gap-1 pt-1" data-testid={`plans-${c.code}`}>
                          {Object.entries(c.plan_breakdown).map(([plan, count]) => (
                            <span key={plan} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${PLAN_CHIP_COLORS[plan] || "bg-slate-100 text-slate-500"}`}>
                              {plan} {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="glass-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-extrabold text-[#0B0F1A] tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</p>
      {sub && <p className="text-[10px] text-[#5B617A] mt-0.5">{sub}</p>}
    </div>
  );
}

function SnapshotCard({ icon: Icon, label, value, sub, gradient, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-[20px] bg-gradient-to-br ${gradient} p-5`}
      style={{ boxShadow: "0 8px 32px rgba(124,92,255,0.18)" }}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/25 backdrop-blur flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold text-white/80 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-extrabold text-white truncate">{value}</div>
          {sub ? <div className="text-[11px] text-white/80 truncate">{sub}</div> : null}
        </div>
      </div>
    </div>
  );
}


/* ── Seed Demo Data Modal ── */
function SeedDemoModal({ onClose, toast }) {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [template, setTemplate] = useState("bakery");
  const [step, setStep] = useState("select"); // select | confirm | loading
  const [result, setResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchTenants = useCallback(async (q) => {
    setSearchLoading(true);
    try {
      const res = await api.get(`/tenants?search=${encodeURIComponent(q)}&page_size=10`);
      setTenants(res.data.items || []);
    } catch { setTenants([]); }
    finally { setSearchLoading(false); }
  }, []);

  useEffect(() => {
    searchTenants("");
  }, [searchTenants]);

  useEffect(() => {
    const timer = setTimeout(() => searchTenants(search), 300);
    return () => clearTimeout(timer);
  }, [search, searchTenants]);

  const handleSeed = async () => {
    setStep("loading");
    try {
      const res = await api.post("/seed-demo-data", {
        tenant_id: selectedTenant.tenant_id,
        template,
      });
      setResult(res.data);
      setStep("done");
      toast.success(`Demo data seeded into "${selectedTenant.name}"`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join("; ") : (typeof d === "string" ? d : "Seed failed");
      toast.error(msg);
      setStep("select");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] w-full max-w-md max-h-[85vh] overflow-y-auto p-6"
        data-testid="seed-demo-modal">

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-extrabold text-[#0B0F1A]">Seed Demo Data</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-[#5B617A]"><X size={18} /></button>
        </div>

        {step === "loading" && (
          <div className="flex flex-col items-center gap-3 py-10">
            <Loader2 className="w-8 h-8 text-[#7C5CFF] animate-spin" />
            <p className="text-sm text-[#5B617A]">Seeding demo data...</p>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <p className="text-sm font-semibold text-emerald-700 mb-2">✓ Demo data seeded successfully</p>
              {result.counts && (
                <div className="text-xs text-emerald-600 space-y-0.5">
                  {Object.entries(result.counts).map(([k, v]) => (
                    <p key={k}><span className="font-semibold">{k}:</span> {v}</p>
                  ))}
                </div>
              )}
              {result.seeded_at && <p className="text-[10px] text-emerald-500 mt-2">Seeded at: {result.seeded_at}</p>}
            </div>
            <button onClick={onClose} data-testid="seed-done-close-btn"
              className="w-full px-4 py-2.5 rounded-xl bg-[#7C5CFF] text-white text-sm font-semibold hover:bg-[#6A4DE0]">
              Done
            </button>
          </div>
        )}

        {step === "select" && (
          <div className="space-y-4">
            {/* Tenant search */}
            <div>
              <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">Select Tenant</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search tenants..."
                  className="input-field pl-8"
                  data-testid="seed-tenant-search"
                />
              </div>
              {selectedTenant && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-[#7C5CFF]/10 text-[#7C5CFF] text-xs font-semibold rounded-lg">
                    {selectedTenant.name}
                    <button onClick={() => setSelectedTenant(null)} className="ml-1.5 hover:text-red-500"><X size={10} /></button>
                  </span>
                </div>
              )}
              <div className="mt-2 max-h-40 overflow-y-auto border border-[#E2E8F0] rounded-xl">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-4"><Loader2 size={16} className="text-[#7C5CFF] animate-spin" /></div>
                ) : tenants.length === 0 ? (
                  <p className="text-xs text-[#5B617A] p-3 text-center">No tenants found</p>
                ) : tenants.map(t => (
                  <button
                    key={t.tenant_id}
                    onClick={() => setSelectedTenant(t)}
                    data-testid={`seed-tenant-option-${t.tenant_id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-[#7C5CFF]/5 transition-colors border-b border-[#F1F5F9] last:border-0 ${selectedTenant?.tenant_id === t.tenant_id ? "bg-[#7C5CFF]/10 font-semibold" : ""}`}
                  >
                    <span className="text-[#0B0F1A]">{t.name}</span>
                    <span className="text-[10px] text-[#5B617A] ml-2">{t.plan || t.plan_key}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Template selector */}
            <div>
              <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">Template</label>
              <div className="flex gap-3">
                {[{ key: "bakery", label: "🧁 Bakery" }, { key: "fmcg", label: "📦 FMCG" }].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setTemplate(key)}
                    data-testid={`seed-template-${key}`}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${template === key ? "border-[#7C5CFF] bg-[#7C5CFF]/5 text-[#7C5CFF]" : "border-[#E2E8F0] text-[#5B617A] hover:border-[#CBD5E1]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <button
              onClick={() => setStep("confirm")}
              disabled={!selectedTenant}
              data-testid="seed-preview-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Database size={15} /> Seed into "{selectedTenant?.name || '...'}"
            </button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-sm font-semibold text-red-700 mb-1">⚠ Warning</p>
              <p className="text-xs text-red-600">
                This will create demo tenants and data in <strong>{selectedTenant?.name}</strong> using the <strong>{template}</strong> template. Continue?
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep("select")}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#5B617A] border border-[#E2E8F0] hover:bg-[#F1F5F9]">
                Cancel
              </button>
              <button onClick={handleSeed} data-testid="seed-confirm-btn"
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors">
                Confirm Seed
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
